import React, { useEffect, useRef, useState } from 'react';
import PlayIcon from './icons/PlayIcon';
import PauseIcon from './icons/PauseIcon';
import StopIcon from './icons/StopIcon';

interface AnimatedPlayIconProps {
  isPlaying: boolean;
  showStopIcon: boolean;
  size?: 'base' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
  playIconClassName?: string;
}

type IconKey = 'play' | 'pause' | 'stop';

const renderIcon = (
  iconKey: IconKey,
  size: AnimatedPlayIconProps['size'],
  className: string,
  playIconClassName: string,
) => {
  switch (iconKey) {
    case 'stop':
      return <StopIcon size={size} className={className} aria-hidden="true" />;
    case 'pause':
      return <PauseIcon size={size} className={className} aria-hidden="true" />;
    case 'play':
    default:
      return <PlayIcon size={size} className={`${className} ${playIconClassName}`} aria-hidden="true" />;
  }
};

const AnimatedPlayIcon: React.FC<AnimatedPlayIconProps> = ({
  isPlaying,
  showStopIcon,
  size = 'md',
  className = '',
  playIconClassName = '',
}) => {
  let iconKey: IconKey = 'play';
  if (showStopIcon) {
    iconKey = 'stop';
  } else if (isPlaying) {
    iconKey = 'pause';
  }

  const [activeIconKey, setActiveIconKey] = useState<IconKey>(iconKey);
  const [exitingIconKey, setExitingIconKey] = useState<IconKey | null>(null);
  const [isActiveEntering, setIsActiveEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const previousIconKeyRef = useRef<IconKey>(iconKey);

  useEffect(() => {
    if (previousIconKeyRef.current === iconKey) {
      return;
    }

    setExitingIconKey(previousIconKeyRef.current);
    setActiveIconKey(iconKey);
    setIsActiveEntering(false);
    setIsExiting(false);
    previousIconKeyRef.current = iconKey;

    const exitFrame = window.requestAnimationFrame(() => setIsExiting(true));
    const enterTimer = window.setTimeout(() => setIsActiveEntering(true), 80);
    const exitTimer = window.setTimeout(() => setExitingIconKey(null), 150);

    return () => {
      window.cancelAnimationFrame(exitFrame);
      window.clearTimeout(enterTimer);
      window.clearTimeout(exitTimer);
    };
  }, [iconKey]);

  return (
    <div
      style={{
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'grid',
        placeItems: 'center',
        overflow: 'hidden',
      }}
    >
      {exitingIconKey && (
        <div
          style={{
            position: 'absolute',
            inset: 0,
            display: 'grid',
            placeItems: 'center',
            transform: isExiting ? 'scale(0)' : 'scale(1)',
            transformOrigin: '50% 50%',
            transition: 'transform 150ms ease-in',
          }}
        >
          {renderIcon(exitingIconKey, size, className, playIconClassName)}
        </div>
      )}
      <div
        style={{
          position: 'absolute',
          inset: 0,
          display: 'grid',
          placeItems: 'center',
          transform: isActiveEntering ? 'scale(1)' : 'scale(0)',
          transformOrigin: '50% 50%',
          transition: 'transform 200ms cubic-bezier(0.34, 1.56, 0.64, 1)',
        }}
      >
        {renderIcon(activeIconKey, size, className, playIconClassName)}
      </div>
    </div>
  );
};

export default AnimatedPlayIcon;
